/* eslint-disable react-hooks/exhaustive-deps */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AlertSquare, Check, CheckCircle, HelpSquare, Hourglass03, XClose } from '@untitled-ui/icons-react';
import { operations } from 'api/schema';
import { Activity, DetailPageTask, FileData, LooseObject, Task } from 'api/types';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { queryClient } from 'configs/queryClient';
import { useUserContext } from 'context/UserContextProvider';
import { downloadFile } from 'helpers/downloadHelper';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useAssignMutation from 'hooks/mutations/useAssignMutation';
import { useUpdateB2CVerificationDocsMutation } from 'hooks/mutations/useUpdateB2CVerificationDocsMutation';
import { useUpdateB2CVerificationStateMutation } from 'hooks/mutations/useUpdateB2CVerificationStateMutation';
import useB2CVerificationRequestQuery from 'hooks/useB2CVerificationRequestQuery';
import useFileQueryV2 from 'hooks/useFileQueryV2';
import { get } from 'lodash';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ActivityLogCard from '../../components/ActivityLogCard';
import { RenderFile } from '../../components/RenderFile';
import { TasksTableCard } from '../../components/TasksTableCard';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { AutomatedVerification } from './aiVerification';
import { AiVerification } from './components/AiVerification';
import Checklist from './components/B2CheckList';
import { B2CVerificationStatusCard } from './components/B2CVerificationStatusCard';

// -------------------- Main Component --------------------

type B2CVerificationVehicleRegistrationCertChecks = keyof NonNullable<
  operations['getB2CVerification']['responses']['200']['content']['application/json']['data']
>['vehicle_registration_cert_checks'];

const B2CVerificationDetailPage = () => {
  // --- Hooks ---
  const { t } = useTranslation();
  const { id: verificationId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { user, opUsers } = useUserContext();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenReject, onOpen: onOpenReject, onClose: onCloseReject } = useDisclosure();

  const topRef = useRef<HTMLDivElement>(null);

  // --- Queries ---

  const b2cVerificationQuery = useB2CVerificationRequestQuery(verificationId);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useB2CVerificationRequestQuery.getKey(verificationId) });
  const tasks = (b2cVerificationQuery.data?.data as { tasks?: DetailPageTask[] } | undefined)?.tasks;

  // --- Mutations ---

  const updateB2CVerificationStateMutationWithRedirect = useUpdateB2CVerificationStateMutation(verificationId ?? '', 'redirect');
  const updateB2CVerificationStateMutationWithoutRedirect = useUpdateB2CVerificationStateMutation(verificationId ?? '', 'no-redirect');

  const updateIdB2CVerificationDocsMutation = useUpdateB2CVerificationDocsMutation(verificationId ?? '', 'id');
  const updateRcB2CVerificationDocsMutation = useUpdateB2CVerificationDocsMutation(verificationId ?? '', 'rc');

  const assignMutation = useAssignMutation(
    //@ts-expect-error
    b2cVerificationQuery.data?.data?.open_task?.id as number,
    useB2CVerificationRequestQuery.getKey(verificationId ?? ''),
  );

  // --- File Download ---
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [downloadDocument, setDownloadDocument] = useState<FileData | undefined>(undefined);
  const [click, setClick] = useState(false);
  const fileQuery = useFileQueryV2(url);

  useEffect(() => {
    downloadFile(downloadDocument, fileQuery.data);
    setClick(false);
  }, [fileQuery.data, click]);

  const [idValid, setIdValid] = useState<string[]>([]);
  const [idErr, setIdErr] = useState<('invalid_type' | 'validity' | 'name_match')[]>([]);
  const [rcValid, setRcValid] = useState<string[]>([]);
  const [rcErr, setRcErr] = useState<B2CVerificationVehicleRegistrationCertChecks[]>([]);

  const [idT, setIdT] = useState(false);
  const [rcT, setRcT] = useState(false);

  // --- Derived Data ---
  const openTask = (b2cVerificationQuery.data?.data as any)?.open_task as Task;
  const hasOpenTask = openTask != null;
  const isOpenTaskAssignedToCurrentUser = openTask?.assignee?.id === user?.id;
  const isVerificationCompleted = b2cVerificationQuery.data?.data?.status === 'approved';
  const isVerificationRejected = b2cVerificationQuery.data?.data?.status === 'rejected';
  const isAutomatedVerification = !!b2cVerificationQuery.data?.data?.automated_verification;
  const automatedVerification = b2cVerificationQuery.data?.data?.automated_verification as unknown as AutomatedVerification;
  const hasAiResult = !!automatedVerification?.overall_result;

  // --- Not Touched Logic ---
  const idNotTouched = !b2cVerificationQuery.data?.data?.id_status && b2cVerificationQuery.data?.data?.id_errors === null;
  const rcNotTouched = !b2cVerificationQuery.data?.data?.vrc_status && b2cVerificationQuery.data?.data?.vrc_errors === null;

  // --- Checklist Data ---
  const idChecksData = b2cVerificationQuery.data?.data?.identification_checks;
  const registrationCertificateData = b2cVerificationQuery.data?.data?.vehicle_registration_cert_checks;

  // --- Checklist Arrays ---
  let allKeysId: string[] = [];
  const idChecklist =
    (idChecksData &&
      Object.keys(idChecksData).map((key) => {
        allKeysId.push(key);
        return Checklist({
          question: (idChecksData as Record<string, { question?: string; title?: string }>)[key]?.question ?? '',
          key,
          title: (idChecksData as Record<string, { question?: string; title?: string }>)[key]?.title ?? '',
          valid: idValid,
          setValid: setIdValid,
          err: idErr,
          //@ts-expect-error
          setErr: setIdErr,
          buttonsLocked: !isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected,
          isAutomatedVerification: isAutomatedVerification,
          isTouched: idT || !idNotTouched,
          setIsTouched: setIdT,
        });
      })) ||
    [];

  let allKeysVrc: string[] = [];
  const registrationCertificateChecklist =
    (registrationCertificateData &&
      Object.keys(registrationCertificateData).map((key) => {
        allKeysVrc.push(key);
        return Checklist({
          question: registrationCertificateData[key as B2CVerificationVehicleRegistrationCertChecks]?.question ?? '',
          key,
          title: registrationCertificateData[key as B2CVerificationVehicleRegistrationCertChecks]?.title ?? '',
          valid: rcValid,
          setValid: setRcValid,
          err: rcErr,
          //@ts-expect-error
          setErr: setRcErr,
          buttonsLocked: !isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected,
          isAutomatedVerification: isAutomatedVerification,
          isTouched: rcT || !rcNotTouched,
          setIsTouched: setRcT,
        });
      })) ||
    [];

  useEffect(() => {
    if (idNotTouched && isAutomatedVerification) {
      if ((b2cVerificationQuery.data?.data?.automated_verification_answers as unknown as LooseObject)?.identification) {
        const identification = (b2cVerificationQuery.data?.data?.automated_verification_answers as unknown as LooseObject).identification;
        const validKeys: string[] = [];
        const errKeys: ('invalid_type' | 'validity' | 'name_match')[] = [];

        Object.entries(identification).forEach(([key, value]) => {
          if (value && typeof value === 'object' && 'is_valid' in value) {
            if (value.is_valid) {
              validKeys.push(key);
            } else {
              // Type assertion if you want to restrict to known error keys, otherwise just use string[]
              errKeys.push(key as 'invalid_type' | 'validity' | 'name_match');
            }
          }
        });

        setIdValid(validKeys);
        setIdErr(errKeys);
      }
    } else if (!idNotTouched) {
      setIdValid(allKeysId.filter((key) => !(b2cVerificationQuery.data?.data?.id_errors ?? []).includes(key)));
      setIdErr(
        ((b2cVerificationQuery.data?.data?.id_errors as string[]) ?? []).filter(
          (err): err is 'invalid_type' | 'validity' | 'name_match' => err === 'invalid_type' || err === 'validity' || err === 'name_match',
        ),
      );
    }
    if (rcNotTouched && isAutomatedVerification) {
      if ((b2cVerificationQuery.data?.data?.automated_verification_answers as unknown as LooseObject)?.vehicle_registration) {
        const vehicleRegistration = (b2cVerificationQuery.data?.data?.automated_verification_answers as unknown as LooseObject).vehicle_registration;
        const validKeys: string[] = [];
        const errKeys: B2CVerificationVehicleRegistrationCertChecks[] = [];

        Object.entries(vehicleRegistration).forEach(([key, value]) => {
          if (value && typeof value === 'object' && 'is_valid' in value) {
            if (value.is_valid) {
              validKeys.push(key);
            } else {
              errKeys.push(key as B2CVerificationVehicleRegistrationCertChecks);
            }
          }
        });

        setRcValid(validKeys);
        setRcErr(errKeys);
      }
    } else if (!rcNotTouched) {
      setRcValid(allKeysVrc.filter((key) => !(b2cVerificationQuery.data?.data?.vrc_errors ?? []).includes(key)));
      setRcErr(
        ((b2cVerificationQuery.data?.data?.vrc_errors as string[]) ?? []).filter((err): err is B2CVerificationVehicleRegistrationCertChecks =>
          Object.keys(registrationCertificateData || {}).includes(err),
        ),
      );
    }
  }, [b2cVerificationQuery?.data]);

  // --- Accordion Default Index State ---
  const [accordionDefaultIndex, setAccordionDefaultIndex] = useState<number[]>([]);

  // --- Update accordionDefaultIndex on relevant changes ---
  useEffect(() => {
    let newIndex: number[] = [];
    if (isVerificationCompleted || isVerificationRejected || !isOpenTaskAssignedToCurrentUser) {
      newIndex = [];
    } else if (b2cVerificationQuery.data?.data?.vrc_status && !b2cVerificationQuery.data?.data?.id_status) {
      newIndex = [0];
    } else if (!b2cVerificationQuery.data?.data?.vrc_status && b2cVerificationQuery.data?.data?.id_status) {
      newIndex = [1];
    } else if (!b2cVerificationQuery.data?.data?.vrc_status && !b2cVerificationQuery.data?.data?.id_status) {
      newIndex = [0, 1];
    } else if (b2cVerificationQuery.data?.data?.vrc_status && b2cVerificationQuery.data?.data?.id_status) {
      newIndex = [];
    }
    setAccordionDefaultIndex(newIndex);
  }, [b2cVerificationQuery.data?.data?.vrc_status, b2cVerificationQuery.data?.data?.id_status, isOpenTaskAssignedToCurrentUser]);

  useEffect(() => {
    if (!idNotTouched && !isAutomatedVerification) {
      setIdValid(allKeysId.filter((key) => !(b2cVerificationQuery.data?.data?.id_errors ?? []).includes(key)));
      setIdErr((b2cVerificationQuery.data?.data?.id_errors as ('invalid_type' | 'validity' | 'name_match')[]) ?? []);
    }
    if (!rcNotTouched && !isAutomatedVerification) {
      setRcValid(allKeysVrc.filter((key) => !(b2cVerificationQuery.data?.data?.vrc_errors ?? []).includes(key)));
      setRcErr((b2cVerificationQuery.data?.data?.vrc_errors as B2CVerificationVehicleRegistrationCertChecks[]) ?? []);
    }
  }, [b2cVerificationQuery.data?.data]);

  // --- Icon Logic ---
  const idIcon = idNotTouched ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.submitted')} placement="right" shouldWrapChildren={true}>
      <Icon as={AlertSquare} ml={2} color="blue.500" fontSize={23} />
    </Tooltip>
  ) : b2cVerificationQuery.data?.data?.id_errors && b2cVerificationQuery.data?.data?.id_errors.length > 0 ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.error')} placement="right" shouldWrapChildren={true}>
      <Icon as={HelpSquare} ml={2} color="orange.500" fontSize={23} />
    </Tooltip>
  ) : b2cVerificationQuery.data?.data?.id_status ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.success')} placement="right" shouldWrapChildren={true}>
      <Icon as={CheckCircle} ml={2} color="green.500" fontSize={23} />
    </Tooltip>
  ) : null;

  const registrationCertificateIcon = rcNotTouched ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.submitted')} placement="right" shouldWrapChildren={true}>
      <Icon as={AlertSquare} ml={2} color="blue.500" fontSize={23} />
    </Tooltip>
  ) : b2cVerificationQuery.data?.data?.vrc_errors && b2cVerificationQuery.data?.data?.vrc_errors.length > 0 ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.error')} placement="right" shouldWrapChildren={true}>
      <Icon as={HelpSquare} ml={2} color="orange.500" fontSize={23} />
    </Tooltip>
  ) : b2cVerificationQuery.data?.data?.vrc_status ? (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.success')} placement="right" shouldWrapChildren={true}>
      <Icon as={CheckCircle} ml={2} color="green.500" fontSize={23} />
    </Tooltip>
  ) : (
    <Tooltip label={t('b2c_verification:details_page.icon_tooltip.pending')} placement="right" shouldWrapChildren={true}>
      <Icon as={Hourglass03} ml={2} fontSize={23} />
    </Tooltip>
  );

  // --- Submit Handlers ---

  const onIdSubmit = (idErr: ('invalid_type' | 'validity' | 'name_match')[]) => {
    if (idErr.length === 0) {
      updateIdB2CVerificationDocsMutation.mutate(
        {
          identification_valid: true,
        },
        {
          onSuccess: () => {
            toast({
              title: t('b2c_verification:details_page.toast.document_saved'),
              status: 'success',
              ...defaultToastOptions,
            });
          },
          onError: (error: any) => {
            toast({
              title: error.message,
              status: 'error',
              ...defaultToastOptions,
            });
          },
        },
      );
    } else if (idErr.length > 0) {
      updateIdB2CVerificationDocsMutation.mutate(
        {
          identification_valid: false,
          identification_errors: idErr,
        },
        {
          onSuccess: () => {
            toast({
              title: t('b2c_verification:details_page.toast.document_saved'),
              status: 'success',
              ...defaultToastOptions,
            });
          },
          onError: (error: any) => {
            toast({
              title: error.message,
              status: 'error',
              ...defaultToastOptions,
            });
          },
        },
      );
    }
  };

  const onRcSubmit = (rcErr: B2CVerificationVehicleRegistrationCertChecks[]) => {
    if (rcErr.length === 0) {
      updateRcB2CVerificationDocsMutation.mutate(
        {
          vehicle_registration_cert_valid: true,
        },
        {
          onSuccess: () => {
            toast({
              title: t('b2c_verification:details_page.toast.document_saved'),
              status: 'success',
              ...defaultToastOptions,
            });
          },
          onError: (error: any) => {
            toast({
              title: error.message,
              status: 'error',
              ...defaultToastOptions,
            });
          },
        },
      );
    } else if (rcErr.length > 0) {
      updateRcB2CVerificationDocsMutation.mutate(
        {
          vehicle_registration_cert_valid: false,
          vehicle_registration_cert_errors: rcErr,
        },
        {
          onSuccess: () => {
            toast({
              title: t('b2c_verification:details_page.toast.document_saved'),
              status: 'success',
              ...defaultToastOptions,
            });
          },
          onError: (error: any) => {
            toast({
              title: error.message,
              status: 'error',
              ...defaultToastOptions,
            });
          },
        },
      );
    }
  };

  // --- Navigation Guards ---

  const workshopIdNumber = Number(verificationId);
  if (isNaN(workshopIdNumber)) {
    navigate('/404', { replace: true });
    return null;
  }

  if (b2cVerificationQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (b2cVerificationQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }
  // --- Render ---
  return (
    <>
      <div ref={topRef} />

      {isVerificationCompleted && (
        <Alert status="success" variant="solid">
          <AlertIcon />
          {t('b2c_verification:verification_details.completed')}
        </Alert>
      )}

      {isVerificationRejected && (
        <Alert status="error" variant="solid">
          <AlertIcon />
          {t('b2c_verification:verification_details.rejected')}
        </Alert>
      )}
      {hasOpenTask && (
        <TaskBannerCard
          queryToInvalidate={queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={openTask?.assignee}
          isLoading={b2cVerificationQuery.isFetching || assignMutation.isPending}
          assign={() =>
            assignMutation.mutate({
              updated_at: get(b2cVerificationQuery.data?.data, 'open_task.updated_at', b2cVerificationQuery.data?.data?.updated_at)!,
            })
          }
          reassign={(user_id) =>
            assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(b2cVerificationQuery.data?.data, 'open_task.updated_at', b2cVerificationQuery.data?.data?.updated_at)!,
            })
          }
          task={openTask}
        />
      )}

      <Card m={6}>
        <VStack p={6} alignItems={'flex-start'} spacing={6}>
          <B2CVerificationStatusCard
            isTaskAssigned={isOpenTaskAssignedToCurrentUser}
            currentStatus={b2cVerificationQuery.data?.data?.status ?? ''}
            workshopVerificationQueryData={b2cVerificationQuery.data?.data ?? {}}
          />
          <Divider color="gray.200" />
          <Heading as="h3" size="xxs">
            {t('b2c_verification:details_page.section_details')}
          </Heading>
          <HStack w="100%">
            <SimpleGrid columns={{ lg: 1, xl: 2, '2xl': 3 }} columnGap="24" rowGap="6" width="100%">
              <HStack>
                <Text fontWeight="600">{t('forms:created_at.label')}:</Text>
                <Text>{dateTimeTransformer(b2cVerificationQuery.data?.data?.created_at ?? '') ?? '-'}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="600">{t('workshop:vin.label')}:</Text>
                <Text>{b2cVerificationQuery.data?.data?.vin ?? '-'}</Text>
              </HStack>

              <HStack>
                <Text fontWeight="600">{t('b2c_verification:service_history.label')}:</Text>
                {b2cVerificationQuery.data?.data?.b2c_service_history_request_id ? (
                  <Link to={`/app/op/b2c-service-history-request/${b2cVerificationQuery.data?.data?.b2c_service_history_request_id}`}>
                    <Text color="blue.500" fontWeight="bold" textDecoration="underline">
                      {b2cVerificationQuery.data?.data?.b2c_service_history_request_user_readable_id}
                    </Text>
                  </Link>
                ) : (
                  '-'
                )}
              </HStack>
              <HStack>
                <Text fontWeight="600">{t('workshop:email.label')}:</Text>
                <Text>{b2cVerificationQuery.data?.data?.email ?? '-'}</Text>
              </HStack>
            </SimpleGrid>
          </HStack>
        </VStack>

        <Stack w="full" direction={['column']} p={6}>
          <Divider color="gray.200" />

          <Heading as="h3" size="xxs" py={6}>
            {t('b2c_verification:details_page.section_documents')}
          </Heading>
          {hasAiResult && <AiVerification verification={automatedVerification} />}

          <Accordion
            defaultIndex={accordionDefaultIndex}
            index={accordionDefaultIndex}
            onChange={(expandedIndex) => {
              if (Array.isArray(expandedIndex)) {
                setAccordionDefaultIndex(expandedIndex);
              } else if (typeof expandedIndex === 'number') {
                setAccordionDefaultIndex([expandedIndex]);
              } else {
                setAccordionDefaultIndex([]);
              }
            }}
            allowMultiple
          >
            <AccordionItem
              style={{
                borderWidth: '1px',
                borderRadius: '6px',
                marginBottom: '16px',
                backgroundColor: '#FAFAFA',
                boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                padding: '12px',
              }}
            >
              <AccordionButton>
                <Flex flex="1" alignItems="center">
                  <Heading as="h3" size="xxs">
                    {t('b2c_verification:documents.id')}
                  </Heading>
                  {idIcon}
                </Flex>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel pb={4}>
                <Flex padding={{ base: 2, lg: 6 }} flex={1}>
                  <Flex minH={400} flex={2} direction={'column'}>
                    <Flex direction="column" justifyContent="space-between" flex={1}>
                      {b2cVerificationQuery.data?.data?.identification_file?.raw?.encodedFile ? (
                        <RenderFile
                          header={t('b2c_verification:documents.id')}
                          // @ts-ignore
                          encodedFile={b2cVerificationQuery.data?.data?.identification_file?.raw?.encodedFile}
                          // @ts-ignore
                          type={b2cVerificationQuery.data?.data?.identification_file?.raw?.type}
                        />
                      ) : (
                        <Flex justifyContent="center" alignItems="center">
                          <Text color="gray.400">{t('b2c_verification:details_page.no_data')}</Text>
                        </Flex>
                      )}
                      <Text fontSize={'x-small'} color={'red.500'} mt={4}>
                        {t('b2c_verification:verification.size_warning')}
                      </Text>
                    </Flex>
                    <Flex mt={4}>
                      <Button
                        colorScheme="blue"
                        variant="primary"
                        onClick={() => {
                          setUrl(`b2c-verification/file/${b2cVerificationQuery.data?.data?.identification_file_id}/download`);
                          setDownloadDocument(b2cVerificationQuery.data?.data?.identification_file as FileData);
                          setClick(true);
                        }}
                      >
                        {t('common:download')}
                      </Button>
                    </Flex>
                  </Flex>
                  <Flex direction="column" justifyContent="space-between" ml={5} flex={1}>
                    <Flex direction="column">
                      <Heading as="h3" size="xxs" mb={5}>
                        {t('b2c_verification:details_page.your_checklist')}
                      </Heading>

                      <HStack spacing={6} mb={4}>
                        <Checkbox
                          isDisabled={!isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected}
                          isChecked={idT && idValid.length === allKeysId.length && allKeysId.length > 0}
                          onChange={() => {
                            if (idT && idValid.length === allKeysId.length && allKeysId.length > 0) {
                              setIdValid([]);
                              setIdErr([]);
                              setIdT(false);
                            } else {
                              setIdValid(allKeysId);
                              setIdErr([]);
                              setIdT(true);
                            }
                          }}
                        >
                          {t('common:select_all')}
                        </Checkbox>
                        <Checkbox
                          isDisabled={!isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected}
                          isChecked={idT && idErr.length === allKeysId.length && allKeysId.length > 0}
                          onChange={() => {
                            if (idT && idErr.length === allKeysId.length && allKeysId.length > 0) {
                              setIdErr([]);
                              setIdValid([]);
                              setIdT(false);
                            } else {
                              setIdErr(allKeysId as ('invalid_type' | 'validity' | 'name_match')[]);
                              setIdValid([]);
                              setIdT(true);
                            }
                          }}
                        >
                          {t('common:reject_all')}
                        </Checkbox>
                      </HStack>

                      {idChecklist}
                    </Flex>

                    <Flex justifyContent="flex-end">
                      <Button
                        disabled={
                          (idValid.length === 0 && idErr.length === 0) ||
                          !isOpenTaskAssignedToCurrentUser ||
                          isVerificationCompleted ||
                          isVerificationRejected
                        }
                        size="md"
                        variant="outline"
                        mr={2}
                        onClick={() => {
                          setIdErr([]);
                          setIdValid([]);
                        }}
                      >
                        {t('b2c_verification:details_page.button_discard')}
                      </Button>
                      <Button
                        colorScheme="blue"
                        variant="primary"
                        size="md"
                        minW={110}
                        disabled={
                          !isOpenTaskAssignedToCurrentUser ||
                          isVerificationCompleted ||
                          isVerificationRejected ||
                          idChecklist.length !== idValid.length + idErr.length ||
                          (b2cVerificationQuery.data?.data?.id_status && idChecklist.length === idValid.length) ||
                          (Array.isArray(idErr) &&
                            Array.isArray(b2cVerificationQuery.data?.data?.id_errors) &&
                            ((idErr.length === 0 && b2cVerificationQuery.data?.data?.id_errors.length === 0) ||
                              (idErr.length === b2cVerificationQuery.data?.data?.id_errors.length &&
                                idErr.every((val) => b2cVerificationQuery.data?.data?.id_errors.includes(val)) &&
                                b2cVerificationQuery.data?.data?.id_errors?.every((val) =>
                                  idErr.includes(val as 'invalid_type' | 'validity' | 'name_match'),
                                ))))
                        }
                        onClick={() => onIdSubmit(idErr)}
                        isLoading={updateIdB2CVerificationDocsMutation.isPending}
                      >
                        {t('common:save')}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem
              style={{
                borderWidth: '1px',
                borderRadius: '6px',
                marginBottom: '16px',
                backgroundColor: '#FAFAFA',
                boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                padding: '12px',
              }}
            >
              <AccordionButton>
                <Flex flex="1" alignItems="center">
                  <Heading as="h3" size="xxs">
                    {t('b2c_verification:documents.registration_certificate')}
                  </Heading>
                  {registrationCertificateIcon}
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <Flex padding={{ base: 2, lg: 6 }} flex={1}>
                  <Flex minH={400} flex={2} direction={'column'}>
                    <Flex direction="column" justifyContent="space-between" flex={1}>
                      {b2cVerificationQuery.data?.data?.vehicle_registration_cert_file?.raw?.encodedFile ? (
                        <RenderFile
                          header={t('b2c_verification:documents.registration_certificate')}
                          // @ts-ignore
                          encodedFile={b2cVerificationQuery.data?.data?.vehicle_registration_cert_file?.raw?.encodedFile}
                          // @ts-ignore
                          type={b2cVerificationQuery.data?.data?.vehicle_registration_cert_file?.raw?.type}
                        />
                      ) : (
                        <Flex justifyContent="center" alignItems="center">
                          <Text color="gray.400">{t('b2c_verification:details_page.no_data')}</Text>
                        </Flex>
                      )}
                      <Text fontSize={'x-small'} color={'red.500'} mt={4}>
                        {t('b2c_verification:verification.size_warning')}
                      </Text>
                    </Flex>
                    <Flex mt={4}>
                      <Button
                        colorScheme="blue"
                        variant="primary"
                        onClick={() => {
                          setUrl(`b2c-verification/file/${b2cVerificationQuery.data?.data?.vehicle_registration_cert_file_id}/download`);
                          setDownloadDocument(b2cVerificationQuery.data?.data?.vehicle_registration_cert_file as FileData);
                          setClick(true);
                        }}
                      >
                        {t('common:download')}
                      </Button>
                    </Flex>
                  </Flex>
                  <Flex direction="column" justifyContent="space-between" ml={5} flex={1}>
                    <Flex direction="column">
                      <Heading as="h3" size="xxs" mb={5}>
                        {t('b2c_verification:details_page.your_checklist')}
                      </Heading>
                      <HStack spacing={6} mb={4}>
                        <Checkbox
                          isDisabled={!isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected}
                          isChecked={rcT && rcValid.length === allKeysVrc.length && allKeysVrc.length > 0}
                          onChange={() => {
                            if (rcT && rcValid.length === allKeysVrc.length && allKeysVrc.length > 0) {
                              setRcValid([]);
                              setRcErr([]);
                              setRcT(false);
                            } else {
                              setRcValid(allKeysVrc);
                              setRcErr([]);
                              setRcT(true);
                            }
                          }}
                        >
                          {t('common:select_all')}
                        </Checkbox>
                        <Checkbox
                          isDisabled={!isOpenTaskAssignedToCurrentUser || isVerificationCompleted || isVerificationRejected}
                          isChecked={rcT && rcErr.length === allKeysVrc.length && allKeysVrc.length > 0}
                          onChange={() => {
                            if (rcT && rcErr.length === allKeysVrc.length && allKeysVrc.length > 0) {
                              setRcErr([]);
                              setRcValid([]);
                              setRcT(false);
                            } else {
                              setRcErr(allKeysVrc as B2CVerificationVehicleRegistrationCertChecks[]);
                              setRcValid([]);
                              setRcT(true);
                            }
                          }}
                        >
                          {t('common:reject_all')}
                        </Checkbox>
                      </HStack>
                      {registrationCertificateChecklist}
                    </Flex>
                    <Flex justifyContent="flex-end">
                      <Button
                        size="md"
                        disabled={
                          (rcValid.length === 0 && rcErr.length === 0) ||
                          !isOpenTaskAssignedToCurrentUser ||
                          isVerificationCompleted ||
                          isVerificationRejected
                        }
                        variant="outline"
                        mr={2}
                        onClick={() => {
                          setRcErr([]);
                          setRcValid([]);
                        }}
                      >
                        {t('b2c_verification:details_page.button_discard')}
                      </Button>
                      <Button
                        colorScheme="blue"
                        variant="primary"
                        size="md"
                        minW={110}
                        isLoading={updateRcB2CVerificationDocsMutation.isPending}
                        disabled={
                          !isOpenTaskAssignedToCurrentUser ||
                          isVerificationCompleted ||
                          isVerificationRejected ||
                          registrationCertificateChecklist.length !== rcValid.length + rcErr.length ||
                          (b2cVerificationQuery?.data?.data?.vrc_status && registrationCertificateChecklist.length === rcValid.length) ||
                          (Array.isArray(rcErr) &&
                            Array.isArray(b2cVerificationQuery?.data?.data?.vrc_errors) &&
                            ((rcErr.length === 0 && b2cVerificationQuery?.data?.data?.vrc_errors.length === 0) ||
                              (rcErr.length === b2cVerificationQuery?.data?.data?.vrc_errors.length &&
                                rcErr.every((val) => b2cVerificationQuery?.data?.data?.vrc_errors.includes(val)) &&
                                b2cVerificationQuery?.data?.data?.vrc_errors?.every((val) =>
                                  rcErr.includes(val as B2CVerificationVehicleRegistrationCertChecks),
                                ))))
                        }
                        onClick={() => onRcSubmit(rcErr)}
                      >
                        {t('common:save')}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          {!(isVerificationCompleted || isVerificationRejected) && (
            <>
              <Divider my={5} />
              <Heading as="h3" size="xxs" pb={6}>
                {t('b2c_verification:details_page.your_actions')}
              </Heading>
              <Flex flexWrap="wrap" justifyContent="flex-end">
                <Button
                  colorScheme="red"
                  rightIcon={<XClose />}
                  size="md"
                  m={1}
                  onClick={onOpenReject}
                  disabled={
                    !isOpenTaskAssignedToCurrentUser ||
                    !(
                      (b2cVerificationQuery.data?.data?.id_errors && b2cVerificationQuery.data?.data?.id_errors.length > 0) ||
                      (b2cVerificationQuery.data?.data?.vrc_errors && b2cVerificationQuery.data?.data?.vrc_errors.length > 0)
                    )
                  }
                >
                  {t('b2c_verification:button.reject')}
                </Button>
                <Button
                  colorScheme="green"
                  rightIcon={<Check />}
                  size="md"
                  m={1}
                  disabled={
                    !isOpenTaskAssignedToCurrentUser || !(b2cVerificationQuery.data?.data?.id_status && b2cVerificationQuery.data?.data?.vrc_status)
                  }
                  onClick={onOpen}
                >
                  {t('b2c_verification:button.approve')}
                </Button>
              </Flex>
            </>
          )}
        </Stack>
      </Card>

      <TasksTableCard tasks={tasks} isLoading={b2cVerificationQuery.isPending} m={6} />
      <ActivityLogCard
        queryKey={useB2CVerificationRequestQuery.getKey(verificationId)}
        resourceKey="b2c_verification_request"
        activities={(b2cVerificationQuery.data?.data as any)?.activities as Activity[]}
        account_id={workshopIdNumber}
        m={6}
      />
      {/* Modals */}
      <Modal closeOnOverlayClick={false} isOpen={isOpenReject} onClose={onCloseReject}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('b2c_verification:reject_verification.modal.header')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>{t('b2c_verification:reject_verification.modal.description')}</ModalBody>
          <ModalFooter>
            <Button onClick={onCloseReject} mr={3} variant="outline">
              {t('b2c_verification:details_page.button_discard')}
            </Button>
            <Button
              isLoading={updateB2CVerificationStateMutationWithoutRedirect.isPending}
              colorScheme="blue"
              onClick={() =>
                updateB2CVerificationStateMutationWithoutRedirect.mutate(
                  { status: 'rejected' },
                  {
                    onSuccess: () => {
                      onCloseReject();
                      setTimeout(() => {
                        topRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 150);
                      toast({
                        title: t('b2c_verification:reject_verification.success_toast'),
                        status: 'success',
                        ...defaultToastOptions,
                      });
                    },
                    onError: (error: any) => {
                      toast({
                        title: error.message,
                        status: 'error',
                        ...defaultToastOptions,
                      });
                    },
                  },
                )
              }
            >
              {t('common:confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxW="850px" w="100%">
          <ModalHeader>{t('b2c_verification:approve_verification.modal.header')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>{t('b2c_verification:approve_verification.modal.description')}</ModalBody>
          <ModalFooter>
            <Button
              onClick={onClose}
              mr={3}
              variant="outline"
              whiteSpace="normal"
              textAlign="center"
              width="100%"
              minW={0}
              height="auto"
              py={3}
              lineHeight="normal"
            >
              {t('b2c_verification:details_page.button_discard')}
            </Button>
            <Button
              whiteSpace="normal"
              textAlign="center"
              width="100%"
              minW={0}
              mr={3}
              height="auto"
              py={3}
              lineHeight="normal"
              isLoading={updateB2CVerificationStateMutationWithoutRedirect.isPending}
              colorScheme="blue"
              onClick={() =>
                updateB2CVerificationStateMutationWithoutRedirect.mutate(
                  { status: 'approved' },
                  {
                    onSuccess: () => {
                      onClose();
                      setTimeout(() => {
                        topRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 150);
                      toast({
                        title: t('b2c_verification:approve_verification.success_toast'),
                        status: 'success',
                        ...defaultToastOptions,
                      });
                    },
                    onError: (error: any) => {
                      toast({
                        title: error.message,
                        status: 'error',
                        ...defaultToastOptions,
                      });
                    },
                  },
                )
              }
            >
              {t('b2c_verification:approve_verification.approve_only')}
            </Button>
            <Button
              whiteSpace="normal"
              textAlign="center"
              width="100%"
              height="auto"
              py={3}
              lineHeight="normal"
              minW={0}
              isLoading={updateB2CVerificationStateMutationWithRedirect.isPending}
              colorScheme="blue"
              onClick={() =>
                updateB2CVerificationStateMutationWithRedirect.mutate(
                  { status: 'approved' },
                  {
                    onSuccess: () => {
                      navigate(`/app/op/b2c-service-history-request/${b2cVerificationQuery.data?.data?.b2c_service_history_request_id}`, {
                        state: { fromApprovedVerification: true },
                      });
                      onClose();
                      setTimeout(() => {
                        topRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 150);
                      toast({
                        title: t('b2c_verification:approve_verification.success_toast'),
                        status: 'success',
                        ...defaultToastOptions,
                      });
                    },
                    onError: (error: any) => {
                      toast({
                        title: error.message,
                        status: 'error',
                        ...defaultToastOptions,
                      });
                    },
                  },
                )
              }
            >
              {t('b2c_verification:approve_verification.approve_continue')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default B2CVerificationDetailPage;
